<template>
  <section class="page">
    <div class="eyebrow">📚 问诊话术 · OPQRST/SAMPLE 训练</div>
    <div class="heading">
      <div>
        <h1>问诊训练 · <em>从脚本 → 实战 → AI 点评</em></h1>
        <p>一键生成标准化问诊话术，再切换训练模式与 AI 导师对练，实时维度反馈</p>
      </div>
    </div>

    <!-- 模式切换 -->
    <div class="tabs" style="margin-top:16px">
      <button :class="{ active: mode === 'script' }" @click="mode = 'script'">📖 话术脚本</button>
      <button :class="{ active: mode === 'practice' }" @click="mode = 'practice'">🗣️ 对练模式</button>
    </div>

    <!-- ============ 话术脚本模式 ============ -->
    <template v-if="mode === 'script'">
      <div class="composer">
        <input v-model="topic" class="ai-input" placeholder="输入主诉，生成标准问诊话术脚本" :disabled="scriptLoading" @keyup.enter="genScript">
        <button class="btn" :disabled="scriptLoading || !topic.trim()" @click="genScript">{{ scriptLoading ? '生成中…' : '📖 生成话术脚本' }}</button>
      </div>
      <example-chips :examples="examples" @select="topic = $event" />

      <gate-banner v-if="gateError" :error="gateError" />
      <empty-state
        v-if="!script && !scriptLoading && !scriptError"
        icon="📖"
        title="标准问诊话术脚本"
        description="系统将按「开场 → 主诉聚焦 → 伴随症状 → 既往史 → 关闭」5 阶段输出完整话术脚本，每阶段给出 2-3 句可直接使用的问诊话术…"
      />
      <ai-output-card v-else title="📖 问诊话术脚本" :loading="scriptLoading" :error="scriptError" :model="scriptResult && scriptResult.model" :mock="scriptResult && scriptResult.mock" :warning="scriptResult && scriptResult.warning">
        <template v-if="script">
          <div class="dim-quick">
            <dimension-card :covered="covered" @fill="s => scriptPick = s" />
          </div>
          <p v-if="scriptPick" class="script-pick">💡 已选取话术：「{{ scriptPick }}」（切到对练模式可直接使用）</p>
          <article v-for="(st, i) in script.stages || []" :key="i" class="stage-card">
            <h3><span class="stage-num">{{ i + 1 }}</span>{{ st.name }}</h3>
            <p v-for="(l, j) in st.lines || []" :key="j" class="stage-line">「{{ l }}」</p>
            <p v-if="st.tip" class="hint">💡 {{ st.tip }}</p>
          </article>
          <button class="btn" style="margin-top:8px" @click="toPractice">🗣️ 拿这个主诉去对练</button>
        </template>
        <template v-else-if="scriptResult && scriptResult.content"><p style="white-space:pre-wrap">{{ scriptResult.content }}</p></template>
      </ai-output-card>
    </template>

    <!-- ============ 对练模式 ============ -->
    <template v-else>
      <template v-if="!started">
        <div class="composer">
          <input v-model="topic" class="ai-input" placeholder="输入主诉，AI 导师将陪你练习问诊" :disabled="starting" @keyup.enter="start">
          <button class="btn" :disabled="starting || !topic.trim()" @click="start">{{ starting ? '创建中…' : '🗣️ 开始对练' }}</button>
        </div>
        <example-chips :examples="examples" @select="topic = $event" />
        <gate-banner v-if="startError" :error="startError" />
        <empty-state icon="🗣️" title="问诊话术训练" description="输入一个主诉，AI 导师将扮演你的问诊教练，和你进行多轮对话练习。训练中点击下方维度卡片，话术自动填入输入框…" />
      </template>
      <div v-else class="training-grid">
        <div class="chat-panel">
          <div class="chat-toolbar">
            <b>🎓 AI 导师 · {{ title }}</b>
            <span v-if="demo" class="badge badge-mock">⚠ 演示数据</span>
            <span class="spacer" />
            <button class="btn btn-ghost btn-sm" @click="restart">重开</button>
          </div>
          <chat-thread :messages="messages" :typing="typing" avatar="🎓" />
          <div v-if="lastFeedback" class="feedback-bar">{{ lastFeedback }}</div>
          <chat-input ref="input" placeholder="输入你的问诊话术…" :disabled="typing" @send="onSend" />
        </div>
        <aside class="side-panel">
          <coverage-tracker :coverage="coverage" :covered="covered" />
          <div class="section-title" style="margin-top:16px">📖 问诊维度速查</div>
          <dimension-card :covered="covered" @fill="fillUtterance" />
        </aside>
      </div>
    </template>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import { callAI } from '../api';
import AIOutputCard from '../components/AIOutputCard.vue';
import EmptyState from '../components/EmptyState.vue';
import GateBanner from '../components/GateBanner.vue';
import ExampleChips from '../components/ExampleChips.vue';
import ChatThread from '../components/ChatThread.vue';
import ChatInput from '../components/ChatInput.vue';
import DimensionCard from '../components/DimensionCard.vue';
import CoverageTracker from '../components/CoverageTracker.vue';

export default {
  name: 'TrainingView',
  components: { AIOutputCard, EmptyState, ExampleChips, ChatThread, ChatInput, DimensionCard, CoverageTracker },
  data: () => ({
    mode: 'script',
    topic: '',
    examples: ['胸痛', '腹痛', '发热', '咯血'],
    // 脚本模式
    scriptLoading: false, scriptError: '', scriptResult: null, scriptPick: '',
    gateError: null,
    // 对练模式
    starting: false,
    startError: null,
  }),
  computed: {
    ...mapState('chat', ['sessionId', 'title', 'messages', 'typing', 'demo', 'covered', 'coverage', 'lastFeedback']),
    started() { return !!this.sessionId; },
    script() { return this.scriptResult && this.scriptResult.parsed; },
  },
  methods: {
    async genScript() {
      if (!this.topic.trim() || this.scriptLoading) return;
      this.scriptLoading = true;
      this.scriptError = '';
      try {
        this.scriptResult = await callAI('training-script', { topic: this.topic.trim() });
      } catch (e) {
        this.gateError = { code: e.code, msg: e.msg || e.message };
        this.scriptError = this.gateError.msg;
      } finally {
        this.scriptLoading = false;
      }
    },
    async start() {
      if (!this.topic.trim() || this.starting) return;
      this.starting = true;
      try {
        await this.$store.dispatch('chat/start', { module: 'training', title: this.topic.trim() });
      } catch (e) {
        this.startError = { code: e.code, msg: `创建会话失败：${e.msg || e.message}` };
      } finally {
        this.starting = false;
      }
    },
    async onSend(text) {
      try {
        await this.$store.dispatch('chat/send', text);
        this.startError = null;
      } catch (e) {
        this.startError = { code: e.code, msg: e.msg || e.message };
      }
    },
    fillUtterance(template) {
      const input = this.$refs.input && this.$refs.input.$el.querySelector('input');
      if (input) { input.value = template; input.focus(); input.dispatchEvent(new Event('input')); }
    },
    toPractice() { this.mode = 'practice'; },
    restart() { this.$store.commit('chat/reset', 'training'); },
  },
};
</script>

<style scoped>
.training-grid { display: grid; grid-template-columns: minmax(0, 1.7fr) minmax(0, 1fr); gap: 18px; margin-top: 18px; }
.chat-panel { background: var(--bg-2); border: 1px solid var(--border-2); border-radius: var(--radius-l); padding: 16px; }
.chat-toolbar { display: flex; align-items: center; gap: 10px; font-size: 13px; margin-bottom: 12px; }
.chat-toolbar .spacer { flex: 1; }
.btn-sm { padding: 4px 12px; font-size: 12px; }
.feedback-bar { background: var(--primary-a08); border: 1px solid var(--primary-a25); color: var(--accent); font-size: 12px; border-radius: var(--radius-s); padding: 8px 12px; margin-top: 10px; }
.stage-card { border: 1px solid var(--border-2); border-radius: var(--radius); padding: 14px 16px; margin-top: 10px; background: var(--bg-2); }
.stage-card h3 { margin: 0 0 8px; font-size: 13px; }
.stage-num { display: inline-grid; place-items: center; width: 20px; height: 20px; background: var(--badge-num-bg); border-radius: 6px; font-size: 11px; margin-right: 8px; }
.stage-line { margin: 5px 0; font-size: 13px; line-height: 1.8; color: var(--text-1); }
.script-pick { background: var(--success-a07); border: 1px dashed var(--success-a40); color: var(--success); font-size: 12px; border-radius: var(--radius-s); padding: 8px 12px; margin-top: 10px; }
@media (max-width: 900px) { .training-grid { grid-template-columns: 1fr; } }
</style>
